/**
 * editHistory — AK-321 undo/redo для редактора контура «Обвести поле».
 *
 * Чистая (без terra-draw/MapLibre) машина истории правок: хранит снимки
 * геометрии редактируемой фигуры и даёт undo/redo. Контроллер
 * (ContourEditorController) дёргает push() на каждое осевшее изменение и
 * undo()/redo() по кнопкам/клавишам, применяя возвращённый снимок к terra-draw.
 *
 * Семантика (design-lead спека):
 *   - push(snapshot): кладёт снимок как «текущий». ЛЮБОЙ push очищает redo-стек
 *     (новая правка обрывает «будущее»). Дубликат текущего снимка игнорируется
 *     (terra-draw эмитит несколько change на один жест).
 *   - undo(): возвращает ПРЕДЫДУЩИЙ снимок (на который надо откатиться) или null,
 *     если откатываться некуда. Текущий уезжает в redo-стек.
 *   - redo(): возвращает следующий снимок из redo-стека или null.
 *   - cap (~50): при переполнении выбрасываем самый старый снимок.
 *
 * Реализация — единый массив `past` + указатель отсутствует: вместо него
 * держим `undo`-стек (включая текущий на вершине) и `redo`-стек. Текущий =
 * вершина undo-стека. Это делает undo/redo симметричными и тривиально
 * тестируемыми.
 */

/**
 * Снимок геометрии редактируемого поля.
 *
 * AK-371: расширен с одного полигона (`number[][][]` = кольца) до
 * MULTIPOLYGON (`number[][][][]` = части[part][ring][pt][lng|lat]). Одно «поле»
 * может состоять из нескольких несмежных частей + дырок (ADR 2026-07-07).
 * Одиночная фигура хранится как мультиполигон из одной части — история
 * единообразна, undo/redo работают на любой сложности геометрии.
 *
 * Уровни вложенности: parts → rings → points → [lng, lat].
 */
export type GeometrySnapshot = number[][][][];

/** Глубокая копия снимка — история не должна разделять ссылки с terra-draw store. */
export function cloneSnapshot(snap: GeometrySnapshot): GeometrySnapshot {
  return snap.map((part) => part.map((ring) => ring.map((coord) => coord.slice())));
}

/** Структурное равенство двух снимков (для дедупа повторных change). */
export function snapshotsEqual(a: GeometrySnapshot, b: GeometrySnapshot): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let p = 0; p < a.length; p++) {
    const pa = a[p];
    const pb = b[p];
    if (pa.length !== pb.length) return false;
    for (let r = 0; r < pa.length; r++) {
      const ra = pa[r];
      const rb = pb[r];
      if (ra.length !== rb.length) return false;
      for (let i = 0; i < ra.length; i++) {
        const ca = ra[i];
        const cb = rb[i];
        if (ca.length !== cb.length) return false;
        for (let j = 0; j < ca.length; j++) {
          if (ca[j] !== cb[j]) return false;
        }
      }
    }
  }
  return true;
}

export class EditHistory {
  /** Стек снимков; вершина (последний) = текущее состояние фигуры. */
  private undoStack: GeometrySnapshot[] = [];
  /** Снимки, отменённые undo и доступные для redo (вершина = ближайший redo). */
  private redoStack: GeometrySnapshot[] = [];

  constructor(private readonly cap = 50) {}

  /** Полный сброс (при новой traceField / clear / deactivate). */
  reset(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  /**
   * Зафиксировать новое состояние фигуры. Очищает redo-стек. Дубликат текущего
   * снимка не пишется (terra-draw шлёт несколько change на один drag). Снимок
   * клонируется — внешний код может мутировать свой массив.
   */
  push(snapshot: GeometrySnapshot): void {
    const top = this.undoStack[this.undoStack.length - 1];
    if (top && snapshotsEqual(top, snapshot)) return;
    this.undoStack.push(cloneSnapshot(snapshot));
    this.redoStack = [];
    if (this.undoStack.length > this.cap) {
      // Выбрасываем самый старый — cap-окно скользит.
      this.undoStack.shift();
    }
  }

  /**
   * Откат на предыдущий снимок. Текущий (вершина undo) уходит в redo,
   * возвращается ставший текущим снимок (клон) или null, если истории нет
   * (остался только базовый снимок / стек пуст).
   */
  undo(): GeometrySnapshot | null {
    if (this.undoStack.length < 2) return null;
    const current = this.undoStack.pop() as GeometrySnapshot;
    this.redoStack.push(current);
    const prev = this.undoStack[this.undoStack.length - 1];
    return cloneSnapshot(prev);
  }

  /**
   * Повтор отменённой правки. Возвращает восстановленный снимок (клон) или null.
   * Восстановленный снимок становится текущим (вершиной undo-стека).
   */
  redo(): GeometrySnapshot | null {
    const next = this.redoStack.pop();
    if (!next) return null;
    this.undoStack.push(next);
    return cloneSnapshot(next);
  }

  get canUndo(): boolean {
    return this.undoStack.length >= 2;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Текущий снимок (вершина undo-стека) или null. Для отладки/тестов. */
  get current(): GeometrySnapshot | null {
    const top = this.undoStack[this.undoStack.length - 1];
    return top ? cloneSnapshot(top) : null;
  }
}
